/**
 * admin Composer 결재란 데이터 빌더 — rc 코어 `buildApprovalBlockData`(`@safetyhub/report-composer`)의 **어댑터**.
 *
 * 2026-09-18 R6까지는 web 코어의 「부분 미러」였다(앱 경계를 넘어 import할 수 없어 admin-local로 복제, F-25·F-34는
 * `/admin/reports/:id` 응답에 재료(`authorSignedAt`·`delegatedContact`)가 없어 미러하지 못함). R6에서 코어를 rc로
 * 올리고 응답 계약을 넓혀 web 인증 상세와 **같은 코어·같은 게이트**로 그린다 — 규칙(정의 기반 결재라인 + 미지정 칸
 * 마커 / 최신 round 서명 / DRAFT 서명 숨김 / F-25 작성 서명 게이트 / F-34 반려 뒤 재작성 / OFFLINE)은 전부 rc
 * 코어 JSDoc 참조. 여기 있는 것은 admin 응답의 flat 작성 서명 필드를 코어 형상(`authorSignature` 객체)으로 모으는
 * 것뿐이다(web `composer/composerApprovalData.ts`와 같은 규칙 — 셋 다 없으면 null).
 *
 * 서버 상세(`GET /admin/reports/:id`)가 내려주는 결재 필드를 입력으로 받는다:
 *   approvalLineDefinition · approvalSignatures · authorSignerName · authorSignatureData · authorSignedAt ·
 *   delegatedContact · approvalSignatureMode
 */
import {
  buildApprovalBlockData,
  type ApprovalBlockAuthorSignature,
  type ApprovalBlockData,
} from '@safetyhub/report-composer';
import type { ApprovalLineDefinitionRow } from '@safetyhub/shared';

/** `/admin/reports/:id`가 내려주는 결재 서명 행(표시에 필요한 필드만). */
export interface AdminApprovalSignature {
  id: string;
  approvalLevel: number;
  status: string;
  signerName?: string | null;
  signerTitle?: string | null;
  stepLabel?: string | null;
  signedAt?: string | null;
  signatureData?: string | null;
  round?: number | null;
}

/** 결재란 빌드 입력 — `/admin/reports/:id` 응답의 부분집합. */
export interface AdminApprovalSource {
  status: string;
  approvalLineDefinition?: ApprovalLineDefinitionRow[] | null;
  approvalSignatures?: AdminApprovalSignature[] | null;
  authorSignerName?: string | null;
  authorSignatureData?: string | null;
  /** 담당자가 「작성 완료」를 누른 시각 — F-25 게이트·F-34 판정 재료(web 인증 상세와 같은 컬럼). */
  authorSignedAt?: string | null;
  /** 위임 담당자 — **존재 여부**만 읽는다(공개 계약과 같은 `{ id }` 형상). 비위임이면 null. */
  delegatedContact?: { id: string } | null;
  approvalSignatureMode?: string | null;
}

/**
 * flat 작성 서명 필드를 공개 계약과 같은 객체로 모은다 — 셋 다 없으면 null
 * (서버 `buildAuthorSignatureView`·web `composerApprovalData.toAuthorSignature`와 같은 규칙).
 */
function toAuthorSignature(report: AdminApprovalSource): ApprovalBlockAuthorSignature | null {
  if (
    report.authorSignerName == null &&
    report.authorSignatureData == null &&
    report.authorSignedAt == null
  ) {
    return null;
  }
  return {
    signerName: report.authorSignerName ?? null,
    signedAt: report.authorSignedAt ?? null,
    signatureData: report.authorSignatureData ?? null,
  };
}

/**
 * Composer 결재란(approvalBlock special)에 주입할 ApprovalBlockData를 admin 상세 응답에서 빌드한다.
 * variant는 node.props.variant가 우선이므로 지정하지 않는다(ApprovalBlockSpecial 기본).
 */
export function buildComposerApprovalData(report: AdminApprovalSource): ApprovalBlockData {
  return buildApprovalBlockData({
    status: report.status,
    approvalLineDefinition: report.approvalLineDefinition,
    approvalSignatures: report.approvalSignatures,
    authorSignature: toAuthorSignature(report),
    delegatedContact: report.delegatedContact ?? null,
    approvalSignatureMode: report.approvalSignatureMode,
  });
}
